// src/components/TeamItemsPage/TeamItemTable.js

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './TeamItemTable.module.css';
import { FaEdit, FaTrash, FaCheckCircle, FaClock, FaFlag, FaTable, FaEye } from 'react-icons/fa';
import { format, differenceInCalendarDays, isBefore, startOfDay } from 'date-fns';
import axios from 'axios';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';

const TeamItemTable = ({
  teamItems,
  programId,
  onItemUpdated,
  onItemDeleted,
  onItemAdded,
  onEditItem,
  onQuickView,
  canManage = false,
  currentUserId = null, // Allow checking if user can modify their own tasks
  selectedItems = new Set(),
  onToggleSelect,
  bulkMode = false,
  onSelectAll,
  onSort,
  currentSort,
  currentSortOrder
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (onSort) {
      onSort(column);
    }
  };

  // Get sort icon for column
  const getSortIcon = (column) => {
    if (currentSort !== column) {
      return <span className={styles.sortIcon}>⇅</span>;
    }
    return currentSortOrder === 'asc' 
      ? <span className={styles.sortIconActive}>↑</span>
      : <span className={styles.sortIconActive}>↓</span>;
  };

  // Handle status change
  const handleStatusChange = async (itemId, newStatus) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/team-items/${itemId}`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`, // Include auth token if required
          },
        }
      );
      onItemUpdated(response.data);
    } catch (err) {
      alert('Failed to update status.');
    }
  };

  // Handle delete action with confirmation modal
  const handleDelete = (itemId) => {
    setItemToDelete(itemId);
    setIsConfirmOpen(true);
  };

  // Confirm deletion
  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        const config = {
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('token') && {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            })
          }
        };

        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/api/team-items/${itemToDelete}`,
          config
        );
        
        onItemDeleted(itemToDelete);
        setItemToDelete(null);
        setIsConfirmOpen(false);
        
      } catch (err) {
        // Handle 404 as success (item already deleted)
        if (err.response?.status === 404) {
          onItemDeleted(itemToDelete);
          setItemToDelete(null);
          setIsConfirmOpen(false);
          return;
        }
        
        let errorMessage = 'Failed to delete assignment';
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        }
        
        alert(errorMessage);
        setItemToDelete(null);
        setIsConfirmOpen(false);
      }
    }
  };

  // Cancel deletion
  const cancelDelete = () => {
    setItemToDelete(null);
    setIsConfirmOpen(false);
  };

  // Group team items by Event
  const groupedByEvent = teamItems.reduce((acc, item) => {
    const eventTitle = item.event ? item.event.title : 'No Event';
    if (!acc[eventTitle]) acc[eventTitle] = [];
    acc[eventTitle].push(item);
    return acc;
  }, {});

  // Determine row color based on Due Date and Status
  const getRowColorClass = (item) => {
    if (!item.dueDate) return '';

    const today = startOfDay(new Date());
    const dueDate = startOfDay(new Date(item.dueDate));

    if (isBefore(dueDate, today) && item.status !== 'Done') {
      return styles.overdue; // Red
    } else {
      const daysLeft = differenceInCalendarDays(dueDate, today);
      if (daysLeft === 1) {
        return styles.oneDayLeft; // Orange
      } else if (daysLeft > 1) {
        return styles.softGreen; // Soft Green
      }
    }
    return '';
  };

  // Determine status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'To Do':
        return <FaClock className={styles.statusIcon} />;
      case 'In Progress':
        return <FaFlag className={styles.statusIcon} />;
      case 'Done':
        return <FaCheckCircle className={styles.statusIcon} />;
      default:
        return null;
    }
  };

  if (teamItems.length === 0) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>
            <FaTable className={styles.tableTitleIcon} />
            Assignments Table
          </h2>
        </div>
        <div className={styles.emptyState}>
          No assignments found to display in table.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeader}>
        <h2 className={styles.tableTitle}>
          <FaTable className={styles.tableTitleIcon} />
          Assignments Table
        </h2>
      </div>
      
      <div className={styles.tableContent}>
        <table className={styles.table}>
          <thead className={styles.tableHead}>
            <tr>
              {bulkMode && canManage && (
                <th className={`${styles.tableHeaderCell} ${styles.colCheckbox}`}>
                  <input
                    type="checkbox"
                    onChange={onSelectAll}
                    checked={teamItems.length > 0 && selectedItems.size === teamItems.length}
                    title="Select All"
                  />
                </th>
              )}
              <th className={`${styles.tableHeaderCell} ${styles.sortableHeader} ${styles.colTitle}`} onClick={() => handleSort('title')}>
                <span className={styles.headerContent}>
                  Title {getSortIcon('title')}
                </span>
              </th>
              <th className={`${styles.tableHeaderCell} ${styles.sortableHeader} ${styles.colStatus}`} onClick={() => handleSort('status')}>
                <span className={styles.headerContent}>
                  Status {getSortIcon('status')}
                </span>
              </th>
              <th className={`${styles.tableHeaderCell} ${styles.sortableHeader} ${styles.colPriority}`} onClick={() => handleSort('priority')}>
                <span className={styles.headerContent}>
                  Priority {getSortIcon('priority')}
                </span>
              </th>
              <th className={`${styles.tableHeaderCell} ${styles.sortableHeader} ${styles.colDueDate}`} onClick={() => handleSort('dueDate')}>
                <span className={styles.headerContent}>
                  Due Date {getSortIcon('dueDate')}
                </span>
              </th>
              <th className={`${styles.tableHeaderCell} ${styles.sortableHeader} ${styles.colAssignee}`} onClick={() => handleSort('assignee')}>
                <span className={styles.headerContent}>
                  Assigned To {getSortIcon('assignee')}
                </span>
              </th>
              <th className={`${styles.tableHeaderCell} ${styles.colEvent}`}>Event</th>
              <th className={`${styles.tableHeaderCell} ${styles.colActions}`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(groupedByEvent).map((eventTitle) => (
              <React.Fragment key={eventTitle}>
                {groupedByEvent[eventTitle].map((item) => (
                  <tr
                    key={item._id}
                    className={`${styles.tableRow} ${getRowColorClass(item)} ${selectedItems.has(item._id) ? styles.selectedRow : ''}`}
                  >
                    {bulkMode && canManage && (
                      <td className={`${styles.tableCell} ${styles.colCheckbox}`}>
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item._id)}
                          onChange={() => onToggleSelect(item._id)}
                        />
                      </td>
                    )}
                    <td className={`${styles.tableCell} ${styles.tableCellTitle} ${styles.colTitle}`}>
                      {item.title}
                    </td>
                    <td className={`${styles.tableCell} ${styles.colStatus}`}>
                      <div className={styles.tableCellStatus}>
                        {/* Allow managers or assigned users to change status */}
                        {(canManage || (currentUserId && item.assignedTo && 
                          (item.assignedTo._id === currentUserId || item.assignedTo === currentUserId))) ? (
                          <select
                            value={item.status}
                            onChange={(e) => handleStatusChange(item._id, e.target.value)}
                            className={styles.statusSelect}
                            aria-label={`Change status for ${item.title}`}
                          >
                            <option value="To Do">To Do</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Done">Done</option>
                          </select>
                        ) : (
                          <span className={`${styles.statusBadge} ${styles[`status${item.status.replace(' ', '')}`]}`}>
                            {getStatusIcon(item.status)}
                            {item.status}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={`${styles.tableCell} ${styles.colPriority}`}>
                      <span
                        className={`${styles.priorityBadge} ${styles[`priority${item.priority}`]}`}
                      >
                        {item.priority}
                      </span>
                    </td>
                    <td className={`${styles.tableCell} ${styles.colDueDate}`}>
                      {item.dueDate
                        ? format(new Date(item.dueDate), 'PPP')
                        : 'No Due Date'}
                    </td>
                    <td className={`${styles.tableCell} ${styles.colAssignee}`}>
                      {item.assignedTo
                        ? `${item.assignedTo.firstName} ${item.assignedTo.lastName}`
                        : 'Unassigned'}
                    </td>
                    <td className={`${styles.tableCell} ${styles.colEvent}`}>{item.event ? item.event.title : 'No Event'}</td>
                    <td className={`${styles.tableCell} ${styles.colActions}`}>
                      {!bulkMode && (
                        <div className={styles.tableCellActions}>
                          <button
                            onClick={() => onQuickView?.(item)}
                            className={`${styles.actionButton} ${styles.viewButton}`}
                            title="Quick View"
                            aria-label={`View assignment titled ${item.title}`}
                          >
                            <FaEye />
                          </button>
                          {canManage && (
                            <>
                              <button
                                onClick={() => onEditItem(item)}
                                className={`${styles.actionButton} ${styles.editButton}`}
                                title="Edit Assignment"
                                aria-label={`Edit assignment titled ${item.title}`}
                              >
                                <FaEdit />
                              </button>
                              <button
                                onClick={() => handleDelete(item._id)}
                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                title="Delete Assignment"
                                aria-label={`Delete assignment titled ${item.title}`}
                              >
                                <FaTrash />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this assignment?"
      />
    </div>
  );
};

TeamItemTable.propTypes = {
  teamItems: PropTypes.array.isRequired,
  programId: PropTypes.string.isRequired,
  onItemUpdated: PropTypes.func.isRequired,
  onItemDeleted: PropTypes.func.isRequired,
  onItemAdded: PropTypes.func.isRequired,
  onEditItem: PropTypes.func.isRequired,
  onQuickView: PropTypes.func,
  canManage: PropTypes.bool,
  currentUserId: PropTypes.string,
  selectedItems: PropTypes.instanceOf(Set),
  onToggleSelect: PropTypes.func,
  bulkMode: PropTypes.bool,
  onSelectAll: PropTypes.func,
  onSort: PropTypes.func,
  currentSort: PropTypes.string,
  currentSortOrder: PropTypes.string
};

export default TeamItemTable;

